import { createSlice } from "@reduxjs/toolkit";
import { normalizeRole } from "../utils/rbacHelper";
import { clearAuthScopedClientState } from "../utils/authSession";

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn("[AUTH-STORAGE] Failed to parse", key, error);
    return null;
  }
};

const normalizeUser = (user) => {
  if (!user || typeof user !== "object") return null;
  return {
    ...user,
    role: normalizeRole(user.role),
  };
};

const initialToken =
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  null;

const initialState = {
  user: normalizeUser(readJson("user")) || null,
  token: initialToken,
  authChecking: Boolean(initialToken),
  hydrated: !initialToken,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    startAuthCheck: (state) => {
      state.authChecking = true;
    },
    finishAuthCheck: (state) => {
      state.authChecking = false;
      state.hydrated = true;
    },
    loginSuccess: (state, action) => {
      const nextUser = normalizeUser(action.payload.user);
      const nextToken = action.payload.token || null;
      const previousUserId = String(state.user?._id || state.user?.id || "");
      const nextUserId = String(nextUser?._id || nextUser?.id || "");
      const shouldResetScopedState =
        previousUserId !== nextUserId || String(state.token || "") !== String(nextToken || "");

      if (shouldResetScopedState) {
        clearAuthScopedClientState();
      }

      state.user = nextUser;
      state.token = nextToken;
      state.authChecking = false;
      state.hydrated = true;
      localStorage.setItem("user", JSON.stringify(state.user));
      localStorage.setItem("token", nextToken);
      localStorage.removeItem("authToken");
    },

    logout: (state) => {
      clearAuthScopedClientState();
      state.user = null;
      state.token = null;
      state.authChecking = false;
      state.hydrated = true;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      localStorage.removeItem("adminFallbackToken");
    },

    setUser: (state, action) => {
      state.user = normalizeUser(action.payload);
      state.authChecking = false;
      state.hydrated = true;
      localStorage.setItem("user", JSON.stringify(state.user));
    },
    setToken: (state, action) => {
      state.token = action.payload || null;
      if (action.payload) localStorage.setItem("token", action.payload);
      else localStorage.removeItem("token");
    },
  },
});

export const {
  loginSuccess,
  logout,
  setUser,
  setToken,
  startAuthCheck,
  finishAuthCheck,
} = authSlice.actions;
export default authSlice.reducer;
